import _extends from "@babel/runtime/helpers/extends";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.string.includes.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.string.starts-with.js";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import i18n from 'i18n';
import { setAlert } from 'actions/alerts';
import { FILE_STATUS_VALUES } from 'actions/file';
import { assetQueryKeys } from 'actions/assets';
import { playlistQueryKeys } from 'actions/playlists';
import { fillParent } from 'styles/mixins';
import createUrl from 'tricks/string/createUrl';
import { redirectToLogin } from 'utils/auth/getLogoutPath';
import getEmbeddedVideo from 'utils/getEmbeddedVideo';
import logoutUser from 'utils/auth/logout';
import { getSessionToken } from 'utils/track';
import { getAssetIcon } from 'utils/icons';
import { TYPE_ZIP } from 'utils/file';
import ImageViewer from 'components/ImageViewer';
import AudioPlayer from 'components/AudioPlayer';
import ExternalLinkPanel from './ExternalLinkPanel';
import DirectDownloadLink from './DirectDownload';
import AudioPlayerWrapper from './AudioPlayerWrapper';
import BrightcovePlayer from './BrightcovePlayer';
import WebVideoPlayer from './WebVideoPlayer';
import PDFViewer from './PDFViewer';
import AutoCompleteWrapper from '../AutoCompleteWrapper';
var Iframe = styled.iframe.withConfig({
  displayName: "Content__Iframe",
  componentId: "sc-fze4n1-0"
})(["", " width:100%;height:100%;"], fillParent);
function handlePostScormSave(_x) {
  return _handlePostScormSave.apply(this, arguments);
}
function _handlePostScormSave() {
  _handlePostScormSave = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2(_ref) {
    var assetId, playlistId, status, queryClient, setAuthUser;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          assetId = _ref.assetId, playlistId = _ref.playlistId, status = _ref.status, queryClient = _ref.queryClient, setAuthUser = _ref.setAuthUser;
          if (status === 200) {
            queryClient.invalidateQueries(assetQueryKeys.singleAsset(assetId));
            if (playlistId) {
              queryClient.invalidateQueries(playlistQueryKeys.mySinglePlaylistContent(playlistId));
              queryClient.invalidateQueries(playlistQueryKeys.mySinglePlaylistDetails(playlistId));
            }
          }
          if (!(status === 401)) {
            _context2.next = 13;
            break;
          }
          _context2.prev = 3;
          _context2.next = 6;
          return logoutUser();
        case 6:
          setAuthUser(null);
          redirectToLogin();
          _context2.next = 13;
          break;
        case 10:
          _context2.prev = 10;
          _context2.t0 = _context2["catch"](3);
          if (_context2.t0 !== null && _context2.t0 !== void 0 && _context2.t0.error) {
            setAlert({
              title: i18n.t('alert_logout_failed')
            });
          }
        case 13:
        case "end":
          return _context2.stop();
      }
    }, _callee2, null, [[3, 10]]);
  }));
  return _handlePostScormSave.apply(this, arguments);
}
function AssetViewerContent(props) {
  var iframe = useRef(null);
  var queryClient = useQueryClient();
  var asset = props.asset,
    downloadUrl = props.downloadUrl,
    autoCompleteEnabled = props.autoCompleteEnabled,
    onLoad = props.onLoad,
    onError = props.onError,
    onComplete = props.onComplete,
    setAuthUser = props.setAuthUser,
    markIframeContentAsLoaded = props.markIframeContentAsLoaded,
    playlistId = props.playlistId;
  var download = asset.download,
    fileFormat = asset.fileFormat,
    type = asset.type,
    locale = asset.locale;
  useEffect(function () {
    function handlePostMessage(_x2) {
      return _handlePostMessage.apply(this, arguments);
    }
    function _handlePostMessage() {
      _handlePostMessage = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(event) {
        var _event$data;
        var payload, _ref2, status;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.t0 = event === null || event === void 0 ? void 0 : (_event$data = event.data) === null || _event$data === void 0 ? void 0 : _event$data.type;
              _context.next = _context.t0 === 'scorm-player:post-save' ? 3 : _context.t0 === 'scorm-player:content-loaded' ? 8 : 10;
              break;
            case 3:
              payload = event.data.payload;
              _ref2 = payload || {}, status = _ref2.status;
              _context.next = 7;
              return handlePostScormSave({
                queryClient: queryClient,
                assetId: asset.id,
                playlistId: playlistId,
                status: status,
                setAuthUser: setAuthUser
              });
            case 7:
              return _context.abrupt("break", 10);
            case 8:
              markIframeContentAsLoaded();
              return _context.abrupt("break", 10);
            case 10:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      return _handlePostMessage.apply(this, arguments);
    }
    window.addEventListener('message', handlePostMessage, false);
    // return a function to execute at unmount
    return function () {
      window.removeEventListener('message', handlePostMessage, false);
    };
  }, [asset.id, markIframeContentAsLoaded, playlistId, queryClient, setAuthUser]);
  function getScormOptions(asset) {
    var collectionPostfix = playlistId && !isNaN(playlistId) ? "/collections/".concat(playlistId) : '';
    var baseUrl = "".concat(window.location.origin, "/api/scorm/assets/").concat(asset.id).concat(collectionPostfix);
    var userId = localStorage.getItem('id');
    return {
      sessionToken: getSessionToken(),
      baseUrl: baseUrl,
      pmo: i18n.t('scorm_message_after_open_popup'),
      pmf: i18n.t('scorm_message_when_cannot_open_popup'),
      userId: userId
    };
  }
  if (!asset.id) return null;
  var assetIcon = getAssetIcon(asset);
  var directDownload = download === 'required';
  var handlers = {
    onLoad: onLoad,
    onError: onError,
    onComplete: onComplete
  };
  var assetUrl = asset.convertedUrl || asset.webUrl;

  // We need to explicitly typecheck here unfortunately, as the database disableDownload field is not 0 for all web links.
  if (directDownload && downloadUrl && type !== 'web') {
    return /*#__PURE__*/React.createElement(DirectDownloadLink, _extends({
      asset: asset,
      link: downloadUrl,
      autoCompleteEnabled: autoCompleteEnabled
    }, handlers));
  }
  // TODO: consider factoring these error-checks into the context provider

  // only happen with the new unzipper flow
  if (asset.fileStatus === FILE_STATUS_VALUES.PROCESSING) {
    onError({
      icon: 'link',
      message: i18n.t('asset_viewer_error_processing')
    });
    return null;
  }
  if (asset.fileStatus === FILE_STATUS_VALUES.INVALID) {
    if ((asset === null || asset === void 0 ? void 0 : asset.uploadErrorCode) === 'INVALID_ASSET_TYPE_ERROR') {
      onError({
        icon: 'link',
        message: i18n.t('asset_viewer_error_invalid')
      });
    } else if (asset.fileFormat === 'video') {
      onError({
        icon: 'link',
        message: i18n.t('asset_viewer_error_video')
      });
    } else {
      onError({
        icon: 'link',
        message: i18n.t('asset_viewer_error_processing_issue')
      });
    }
    return null;
  }
  if (!assetUrl) {
    onError({
      icon: 'link',
      message: i18n.t('asset_viewer_error_visible')
    });
    return null;
  }
  var assetContent;
  var assetType = ['web', 'scorm', TYPE_ZIP].includes(asset.type) ? asset.type : null;
  if (!assetType) {
    var isPdf = asset.convertedFileType === 'pdf' && asset.converted;
    assetType = isPdf ? 'pdf' : fileFormat || type;
  }
  var withAutoComplete = function withAutoComplete(component) {
    return /*#__PURE__*/React.createElement(AutoCompleteWrapper, {
      autoCompleteEnabled: autoCompleteEnabled,
      userContentStatus: asset.userContentStatus,
      onComplete: onComplete
    }, component);
  };
  switch (assetType) {
    case 'pdf':
      {
        assetContent = withAutoComplete(/*#__PURE__*/React.createElement(PDFViewer, _extends({
          src: assetUrl,
          title: asset.name
        }, handlers)));
        break;
      }
    case 'image':
      {
        assetContent = withAutoComplete(/*#__PURE__*/React.createElement(ImageViewer, _extends({
          imageUrl: assetUrl,
          alternativeText: asset.alternativeText
        }, handlers)));
        break;
      }
    case 'audio':
      {
        assetContent = /*#__PURE__*/React.createElement(AudioPlayerWrapper, {
          icon: assetIcon,
          pictureUrl: asset.pictureUrl
        }, /*#__PURE__*/React.createElement(AudioPlayer, _extends({
          url: assetUrl,
          locale: locale,
          autoCompleteEnabled: autoCompleteEnabled,
          userContentStatus: asset.userContentStatus
        }, handlers)));
        break;
      }
    case 'video':
      {
        if (!asset.brightcoveAccountId) {
          onError({
            icon: 'link',
            message: i18n.t('asset_viewer_error_video_unavailable')
          });
          return null;
        }
        assetContent = /*#__PURE__*/React.createElement(BrightcovePlayer, _extends({
          key: "video-".concat(asset.brightcoveAccountId, "-").concat(asset.brightcoveVideoId),
          accountId: asset.brightcoveAccountId,
          videoId: asset.brightcoveVideoId,
          userContentStatus: asset.userContentStatus,
          autoCompleteEnabled: autoCompleteEnabled
        }, handlers));
        break;
      }
    default:
      {
        // This was a part of a routing system to attach additional parameters to the URL.
        var src;
        if (assetType === 'web') {
          src = "/api/url?url=".concat(encodeURIComponent(assetUrl));
        } else if (assetType === 'scorm') {
          src = createUrl(assetUrl, getScormOptions(asset));
        } else if (assetType === TYPE_ZIP) {
          // Microsite
          // Include the hash in the source URL
          src = "".concat(assetUrl).concat(window.location.hash);
        } else {
          src = assetUrl;
        }

        // Scorm player controls displaying content in popup/iframe via its own settings called 'openContentIn'
        // Force insecure web links to open in a new window
        var openAsExternalLink = asset.openInIframe ? assetType === 'web' && !assetUrl.toLowerCase().startsWith('https://') : assetType !== 'scorm';
        if (openAsExternalLink) {
          assetContent = /*#__PURE__*/React.createElement(ExternalLinkPanel, _extends({
            asset: asset,
            link: src,
            autoCompleteEnabled: autoCompleteEnabled
          }, handlers));
        } else {
          var embeddedVideoUrl = getEmbeddedVideo(assetUrl);

          // Use the web video player if the video is hosted in one of the supported video hosts
          if (embeddedVideoUrl) {
            assetContent = /*#__PURE__*/React.createElement(WebVideoPlayer, _extends({
              key: "web-video-".concat(asset.id),
              src: embeddedVideoUrl,
              userContentStatus: asset.userContentStatus,
              autoCompleteEnabled: autoCompleteEnabled
            }, handlers));
          } else {
            assetContent = /*#__PURE__*/React.createElement(Iframe, _extends({
              ref: iframe,
              src: src,
              title: asset.name,
              allow: "camera *; microphone *; autoplay *"
            }, handlers));
            if (assetType !== 'scorm') {
              assetContent = withAutoComplete(assetContent);
            }
          }
        }
        break;
      }
  }
  return assetContent;
}
export default AssetViewerContent;